import { useMemo, useRef, useEffect } from 'react';
import { useTranslate } from '@tolgee/react';
import { useCreation, useEventListener } from 'ahooks';
import { message } from 'antd';
import { eventBus } from '../../../../services/eventBus';
import useIM from '../../../../hooks/business/im/useIM';
import { useConceptText } from '../../../../components/TemplateEditor/renders/TagConcept';
import { useResultTypeLabel } from 'views/Todo/hooks/useLabel';
import { getCurrencySymbol } from '../../../../utils/getCurrencySymbol';
import { isMac } from 'utils/is';
import { resourceURLToFile } from 'utils/getImage';
import { useSendFileVideo } from '../../Chat/components/MessageEditor/Toolbar/VideoUpload/hooks/useUploadVideo';
import { v4 as uuidv4 } from 'uuid';
import { getVideoMetadata } from '../../../../utils/validator/video';
import { useSendFileImage } from '../../Chat/components/MessageEditor/Toolbar/ImageUpload';
import posthog from '@leyan/lytics';
import { parseMustache } from './TemplateNode';

export interface UseTemplateNodeOptions {
  active: boolean;
  showLabel?: boolean;
  submitEffect?: boolean;
  doubleMode?: boolean;
  /** 用于 posthog 等埋点，可选 */
  todoId?: string;
}

export interface UseTemplateNodeFormBridge {
  formValues: Todo.TodoInstance;
  setValues: (values: Todo.TodoInstance) => void;
  handleSubmit: () => void;
}

export interface UseTemplateNodeParams {
  conversationId: string;
  readonly: boolean;
  disabled?: boolean;
  currency?: string;
  tradeAmount?: string | number;
  formBridge?: UseTemplateNodeFormBridge;
}

export function useTemplateNode(
  template: Todo.PolicyTemplate,
  options: UseTemplateNodeOptions,
  params: UseTemplateNodeParams,
) {
  const { active, submitEffect = true, doubleMode = false, todoId } = options;
  const {
    conversationId,
    readonly,
    disabled: disabledParam,
    currency = '',
    tradeAmount,
    formBridge,
  } = params;

  const { t } = useTranslate();
  const { getResultTypeLabel } = useResultTypeLabel();
  const { getConceptText } = useConceptText();
  const { sendText } = useIM();
  const sendFileImage = useSendFileImage();
  const sendFileVideo = useSendFileVideo();

  const disabled = disabledParam ?? readonly ?? false;
  const pValues = formBridge?.formValues;
  const setValues = formBridge?.setValues;
  const handleSubmit = formBridge?.handleSubmit;

  const originMt = useCreation(() => {
    return parseMustache(template.executeContext.content, t);
  }, [template.executeContext.content, t]);

  const mt = useCreation(() => {
    return parseMustache(template.executeContext.translatedContent, t);
  }, [template.executeContext.translatedContent, t]);

  const initialValues = useMemo(() => {
    return originMt.concepts.reduce((acc, cur) => {
      acc[cur.id] = '';
      return acc;
    }, {} as Record<string, string>);
  }, [originMt]);

  const initialTouched = useCreation(() => {
    return originMt.concepts.reduce((acc, cur) => {
      acc[cur.id] = true;
      return acc;
    }, {} as Record<string, boolean>);
  }, [originMt]);

  const needValidateRef = useRef(false);
  const tempResultTypeStatusRef = useRef<Todo.ResultType | undefined>(undefined);
  const inputRef = useRef<any>(null);

  const unit = useMemo(() => getCurrencySymbol(currency), [currency]);

  const mtHasValue = useMemo(() => !!mt.renderString(), [mt]);
  const mtToShow = mtHasValue ? mt : originMt;
  const mtToShowNext = doubleMode ? originMt : undefined;
  const mtToSend = mtToShow;
  const mtToEdit = doubleMode ? mtToShowNext || mtToShow : mtToShow;

  useEffect(() => {
    if (!formBridge) return;
    const handler = () => {
      if (tempResultTypeStatusRef.current && setValues && pValues && handleSubmit) {
        setValues({
          ...pValues,
          resultTypeStatus: tempResultTypeStatusRef.current,
        });
        handleSubmit();
        tempResultTypeStatusRef.current = undefined;
      }
    };
    eventBus.on('UPDATE_DRAFT_CALLBACK', handler);
    return () => eventBus.off('UPDATE_DRAFT_CALLBACK', handler);
  }, [formBridge, pValues, setValues, handleSubmit]);

  useEffect(() => {
    const input = inputRef.current;
    if (active) {
      input?.focus?.();
    } else if (input) {
      eventBus.emit('MESSAGE_EDITOR_FOCUS');
    }
    return () => {
      if (active && input) {
        eventBus.emit('MESSAGE_EDITOR_FOCUS');
      }
    };
  }, [active]);

  const sendEditRef = useRef<{ send: () => void; edit: () => void } | null>(null);

  const send = (
    validateForm: () => Promise<Record<string, unknown>>,
    addonUnitValues: Record<string, string>,
  ) => {
    needValidateRef.current = true;
    return validateForm().then((errors) => {
      if (Object.keys(errors as Record<string, string>).length > 0) {
        inputRef.current?.focus?.();
        return Promise.reject();
      }
      const text = mtToSend.renderString(addonUnitValues);
      const images = mtToSend.images;
      const videos = mtToSend.videos;
      images.forEach(async (url) => {
        const file = await resourceURLToFile(url);
        sendFileImage(file);
      });
      videos.forEach(async (url) => {
        const file = await resourceURLToFile(url);
        getVideoMetadata(url).then(({ width, height }) => {
          const uuid = uuidv4();
          sendFileVideo(file, width, height, uuid);
        });
      });
      return sendText(conversationId, text, undefined).finally(() => {
        eventBus.emit('ON_SEND_MESSAGE');
        if (submitEffect && setValues && pValues && handleSubmit) {
          setValues({
            ...pValues,
            resultTypeStatus: template.resultType,
          });
          handleSubmit();
        }
      });
    });
  };

  const edit = (
    validateForm: () => Promise<Record<string, unknown>>,
    addonUnitValues: Record<string, string>,
  ) => {
    needValidateRef.current = false;
    validateForm().then(() => {
      eventBus.emit('UPDATE_DRAFT', mtToEdit.renderString(addonUnitValues));
      if (mtToEdit.images.length > 0 || mtToEdit.videos.length > 0) {
        message.warn(t('scene.chatWorkspace.taskTab.erpOrderForm.hint.editImageVideoNotSupported'));
      }
      if (submitEffect) {
        tempResultTypeStatusRef.current = template.resultType;
      }
    });
  };

  useEventListener('keydown', (e) => {
    if (!active) return;
    const { key, altKey, metaKey } = e;
    if (isMac ? metaKey : altKey) {
      if (key === '\\') {
        sendEditRef.current?.edit();
        e.preventDefault();
        posthog.capture('$hotKey', {
          hotKey: `${isMac ? 'Cmd' : 'Alt'}+\\`,
          todoId: todoId ?? '',
        });
      } else if (key === 'Enter') {
        sendEditRef.current?.send();
        e.preventDefault();
        posthog.capture('$hotKey', {
          hotKey: `${isMac ? 'Cmd' : 'Alt'}+Enter`,
          todoId: todoId ?? '',
        });
      }
    }
  });

  const hotkey = isMac ? 'cmd' : 'alt';
  const resultTypeLabel = getResultTypeLabel(template.resultType);
  const hasEditContent = !!mtToEdit.renderString();

  return {
    originMt,
    mtToShow,
    mtToShowNext,
    mtToEdit,
    unit,
    concepts: originMt.concepts,
    initialValues,
    initialTouched,
    needValidateRef,
    inputRef,
    getConceptText,
    resultTypeLabel,
    disabled,
    hotkey,
    sendEditRef,
    send,
    edit,
    tradeAmount,
    hasEditContent,
  };
}
